import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  ArrowLeft,
  CheckCircle2,
  Eye,
  EyeOff,
  LockKeyhole,
  LogOut,
  Phone,
  ShieldCheck,
  UserRoundCheck,
} from "lucide-react";

import i18next from "i18next";
import { App as CapApp } from "@capacitor/app";

import NativeSafeSelect from "./ui/NativeSafeSelect.jsx";
import {
  reisorProDriverRequest,
  reisorProMarketRequest,
} from "../utils/reisorProMarketHttp.js";
import {
  fetchUzLegalBundle,
  findLegalDocument,
  legalDocumentUrl,
  legalDocumentsByObligation,
} from "../services/legalV5.js";
import {
  clearPhoneVerificationReturnSignal,
  hasFreshPhoneVerificationReturnSignal,
} from "../utils/phoneVerificationReturnSignal.js";

import "./ReisorProMarketAuth.css";
import ReisorProThemeToggle from "./ReisorProThemeToggle.jsx";
import ReisorProMarketBoard from "./ReisorProMarketBoard.jsx";

const LEGACY_TOKEN_KEY =
  "reisor_pro_market_token";

const TOKEN_PREFIX =
  "reisor_pro_market_token_v2";

function tokenKeyForRole(role) {
  const normalized =
    String(role || "")
      .trim()
      .toUpperCase();

  return `${TOKEN_PREFIX}:${normalized === "EXPEDITOR" ? "EXPEDITOR" : "CARGO"}`;
}

function telegramNativeMiniAppUrl(value) {
  try {
    const url = new URL(
      String(value || "").trim()
    );

    const host =
      String(url.hostname || "")
        .toLowerCase();

    if (
      host !== "t.me" &&
      host !== "telegram.me" &&
      host !== "www.t.me"
    ) {
      return "";
    }

    const parts =
      url.pathname
        .split("/")
        .map((item) => item.trim())
        .filter(Boolean);

    const botUsername =
      String(parts[0] || "")
        .replace(/^@+/, "");

    if (!/^[A-Za-z0-9_]{5,32}$/.test(botUsername)) {
      return "";
    }

    const params =
      new URLSearchParams();

    params.set(
      "domain",
      botUsername
    );

    const shortName =
      String(parts[1] || "");

    if (shortName) {
      params.set(
        "appname",
        shortName
      );
    }

    for (const key of [
      "startapp",
      "mode",
    ]) {
      const raw =
        url.searchParams.get(key);

      if (raw) {
        params.set(key, raw);
      }
    }

    return `tg://resolve?${params.toString()}`;
  } catch {
    return "";
  }
}

const COUNTRIES = [
  {
    code: "UZ",
    prefix: "+998",
    label: "Uzbekistan",
  },
  {
    code: "RU",
    prefix: "+7",
    label: "Россия",
  },
  {
    code: "KZ",
    prefix: "+7",
    label: "Қазақстан",
  },
  {
    code: "KG",
    prefix: "+996",
    label: "Кыргызстан",
  },
  {
    code: "TJ",
    prefix: "+992",
    label: "Тоҷикистон",
  },
  {
    code: "TM",
    prefix: "+993",
    label: "Türkmenistan",
  },
  {
    code: "TR",
    prefix: "+90",
    label: "Türkiye",
  },
  {
    code: "CN",
    prefix: "+86",
    label: "中国",
  },
  {
    code: "AF",
    prefix: "+93",
    label: "Afghanistan",
  },
];

/* REISOR_PRO_SUPPORT_LINK_V1 */

const REISOR_PRO_SUPPORT_URL =
  "mailto:support@cargo-platform.online";

function openReisorProSupport() {
  try {
    const opened =
      window.open(
        REISOR_PRO_SUPPORT_URL,
        "_blank",
        "noopener,noreferrer"
      );

    if (!opened) {
      window.location.href =
        REISOR_PRO_SUPPORT_URL;
    }
  } catch {
    try {
      window.location.href =
        REISOR_PRO_SUPPORT_URL;
    } catch {}
  }
}

const COPY = {
  ru: {
    back: "Назад",
    brandContext: "REISOR PRO · REISOR HUB",
    cargo: "Грузовладелец",
    expeditor: "Экспедитор",

    registerTitle: "Быстрая регистрация",
    registerText:
      "Только номер телефона и пароль — ничего лишнего.",

    loginTitle: "Вход",
    loginText:
      "Введите телефон и пароль.",

    authChoiceTitle:
      "Войти или регистрация",

    authChoiceText:
      "Уже зарегистрированы — войдите. Новый пользователь — регистрация.",

    authChoiceLogin:
      "Войти",

    authChoiceRegister:
      "Регистрация",

    backToChoice:
      "Войти / Регистрация",

    support:
      "Служба поддержки REISOR",

    country: "Страна",
    phone: "Телефон",
    phonePlaceholder: "90 123 45 67",

    password: "Пароль",
    passwordPlaceholder:
      "Минимум 6 символов",

    passwordConfirm:
      "Повторите пароль",

    register:
      "Зарегистрироваться",

    login:
      "Войти",

    already:
      "Уже зарегистрированы?",

    loginLink:
      "Войти",

    noAccount:
      "Нет аккаунта?",

    registerLink:
      "Зарегистрироваться",

    safety:
      "Пароль хранится в защищённом виде. При регистрации номер подтверждается через Telegram без SMS-кода.",

    mismatch:
      "Пароли не совпадают.",

    passwordShort:
      "Пароль должен содержать минимум 6 символов.",

    phoneRequired:
      "Введите номер телефона.",

    phoneVerifyButton:
      "Подтвердить номер",

    phoneVerifyOpening:
      "Открываем Telegram…",

    phoneVerifyPending:
      "Подтвердите номер в Telegram.",

    phoneVerifyVerified:
      "Номер подтверждён",

    phoneVerifyRequired:
      "Сначала подтвердите номер через Telegram.",

    phoneVerifyExpired:
      "Подтверждение истекло. Запустите его заново.",

    phoneVerifyMismatch:
      "Номер Telegram не совпадает с указанным номером.",

    phoneVerifyFailed:
      "Не удалось подтвердить номер через Telegram.",

    loading:
      "Подождите…",

    accountReady:
      "Вход выполнен",

    accountReadyText:
      "Аккаунт REISOR PRO готов. Следующим этапом здесь откроется REISOR HUB.",

    logout:
      "Выйти",

    apiUnavailable:
      "Сервис регистрации пока не подключён к production.",

    badCredentials:
      "Неверный телефон или пароль.",

    exists:
      "Такой аккаунт уже зарегистрирован.",

    tooMany:
      "Слишком много попыток. Попробуйте немного позже.",

    generic:
      "Не удалось выполнить запрос. Попробуйте ещё раз.",
  },

  uz: {
    back: "Орқага",
    brandContext: "REISOR PRO · Юклар бозори",
    cargo: "Юк эгаси",
    expeditor: "Экспедитор",

    registerTitle:
      "Тез рўйхатдан ўтиш",

    registerText:
      "Фақат телефон рақами ва пароль.",

    loginTitle: "Кириш",
    loginText:
      "Телефон ва парольни киритинг.",

    authChoiceTitle:
      "Кириш ёки рўйхатдан ўтиш",

    authChoiceText:
      "Аккаунтингиз бор бўлса — киринг. Янги фойдаланувчи — рўйхатдан ўтиш.",

    authChoiceLogin:
      "Кириш",

    authChoiceRegister:
      "Рўйхатдан ўтиш",

    backToChoice:
      "Кириш / Рўйхатдан ўтиш",

    support:
      "REISOR қўллаб-қувватлаш",

    country: "Давлат",
    phone: "Телефон",
    phonePlaceholder: "90 123 45 67",

    password: "Пароль",
    passwordPlaceholder:
      "Камида 6 та белги",

    passwordConfirm:
      "Паролни такрорланг",

    register:
      "Рўйхатдан ўтиш",

    login:
      "Кириш",

    already:
      "Аккаунтингиз борми?",

    loginLink:
      "Кириш",

    noAccount:
      "Аккаунт йўқми?",

    registerLink:
      "Рўйхатдан ўтиш",

    safety:
      "Пароль ҳимояланган ҳолда сақланади. Рўйхатдан ўтишда рақам Telegram орқали SMS-кодсиз тасдиқланади.",

    mismatch:
      "Пароллар бир хил эмас.",

    passwordShort:
      "Пароль камида 6 та белгидан иборат бўлиши керак.",

    phoneRequired:
      "Телефон рақамини киритинг.",

    phoneVerifyButton:
      "Рақамни тасдиқлаш",

    phoneVerifyOpening:
      "Telegram очилмоқда…",

    phoneVerifyPending:
      "Рақамни Telegram орқали тасдиқланг.",

    phoneVerifyVerified:
      "Рақам тасдиқланди",

    phoneVerifyRequired:
      "Аввал рақамни Telegram орқали тасдиқланг.",

    phoneVerifyExpired:
      "Тасдиқлаш муддати тугади. Қайта бошланг.",

    phoneVerifyMismatch:
      "Telegram рақами киритилган рақамга мос эмас.",

    phoneVerifyFailed:
      "Telegram орқали рақамни тасдиқлаб бўлмади.",

    loading:
      "Кутинг…",

    accountReady:
      "Кириш бажарилди",

    accountReadyText:
      "REISOR PRO аккаунти тайёр. Кейинги босқичда бу ерда Юклар бозори очилади.",

    logout:
      "Чиқиш",

    apiUnavailable:
      "Рўйхатдан ўтиш хизмати ҳали production'га уланмаган.",

    badCredentials:
      "Телефон ёки пароль нотўғри.",

    exists:
      "Бу аккаунт аввал рўйхатдан ўтган.",

    tooMany:
      "Жуда кўп уриниш. Бироздан кейин қайта урининг.",

    generic:
      "Сўровни бажариб бўлмади. Қайта урининг.",
  },

  tr: {
    back: "Geri",
    brandContext: "REISOR PRO · Yük Pazarı",
    cargo: "Yük sahibi",
    expeditor: "Nakliye organizatörü",

    registerTitle: "Hızlı kayıt",
    registerText:
      "Sadece telefon numarası ve şifre.",

    loginTitle: "Giriş",
    loginText:
      "Telefon ve şifrenizi girin.",

    authChoiceTitle:
      "Giriş veya kayıt",

    authChoiceText:
      "Hesabınız varsa giriş yapın. Yeni kullanıcıysanız kayıt olun.",

    authChoiceLogin:
      "Giriş yap",

    authChoiceRegister:
      "Kayıt ol",

    backToChoice:
      "Giriş / Kayıt",

    support:
      "REISOR destek",

    country: "Ülke",
    phone: "Telefon",
    phonePlaceholder: "532 123 45 67",

    password: "Şifre",
    passwordPlaceholder:
      "En az 6 karakter",

    passwordConfirm:
      "Şifreyi tekrar girin",

    register: "Kayıt ol",
    login: "Giriş yap",

    already:
      "Zaten hesabınız var mı?",

    loginLink:
      "Giriş yap",

    noAccount:
      "Hesabınız yok mu?",

    registerLink:
      "Kayıt ol",

    safety:
      "Şifreniz güvenli biçimde saklanır. Kayıtta telefon numarası Telegram üzerinden SMS kodu olmadan doğrulanır.",

    mismatch:
      "Şifreler eşleşmiyor.",

    passwordShort:
      "Şifre en az 6 karakter olmalıdır.",

    phoneRequired:
      "Telefon numaranızı girin.",

    phoneVerifyButton:
      "Numarayı doğrula",

    phoneVerifyOpening:
      "Telegram açılıyor…",

    phoneVerifyPending:
      "Numarayı Telegram'da doğrulayın.",

    phoneVerifyVerified:
      "Numara doğrulandı",

    phoneVerifyRequired:
      "Önce numarayı Telegram ile doğrulayın.",

    phoneVerifyExpired:
      "Doğrulama süresi doldu. Yeniden başlatın.",

    phoneVerifyMismatch:
      "Telegram numarası girilen numarayla eşleşmiyor.",

    phoneVerifyFailed:
      "Numara Telegram ile doğrulanamadı.",

    loading:
      "Lütfen bekleyin…",

    accountReady:
      "Giriş başarılı",

    accountReadyText:
      "REISOR PRO hesabınız hazır. Sonraki aşamada Yük Pazarı burada açılacak.",

    logout:
      "Çıkış",

    apiUnavailable:
      "Kayıt servisi henüz production ortamına bağlanmadı.",

    badCredentials:
      "Telefon veya şifre yanlış.",

    exists:
      "Bu hesap zaten kayıtlı.",

    tooMany:
      "Çok fazla deneme. Biraz sonra tekrar deneyin.",

    generic:
      "İşlem tamamlanamadı. Tekrar deneyin.",
  },
};


const LEGAL_COPY = {
  ru: {
    accept: "Я принимаю",
    cargoOffer: "оферту грузовладельца",
    expeditorOffer: "оферту экспедитора",
    and: "и",
    privacy: "политику конфиденциальности",
    platformRules: "и правила платформы REISOR",
    pdConsent: "Даю отдельное согласие на обработку персональных данных.",
    openPdConsent: "Открыть согласие",
    required:
      "Для регистрации примите пользовательское соглашение и отдельно дайте согласие на обработку персональных данных.",
    close: "Закрыть",
    loading: "Загрузка документа…",
    loadError: "Не удалось загрузить документ.",
  },

  uz: {
    accept: "Қабул қиламан",
    cargoOffer: "юк эгаси офертасини",
    expeditorOffer: "экспедитор офертасини",
    and: "ва",
    privacy: "махфийлик сиёсатини",
    platformRules: "ва REISOR платформа қоидаларини",
    pdConsent: "Шахсга доир маълумотларни қайта ишлашга алоҳида розилик бераман.",
    openPdConsent: "Розиликни очиш",
    required:
      "Рўйхатдан ўтиш учун фойдаланувчи келишувини қабул қилинг ва шахсга доир маълумотларни қайта ишлашга алоҳида розилик беринг.",
    close: "Ёпиш",
    loading: "Ҳужжат юкланмоқда…",
    loadError: "Ҳужжатни юклаб бўлмади.",
  },

  tr: {
    accept: "Kabul ediyorum",
    cargoOffer: "yük sahibi sözleşmesini",
    expeditorOffer: "nakliye organizatörü sözleşmesini",
    and: "ve",
    privacy: "gizlilik politikasını",
    platformRules: "ve REISOR platform kurallarını",
    pdConsent: "Kişisel verilerimin işlenmesine ayrıca onay veriyorum.",
    openPdConsent: "Onayı aç",
    required:
      "Kayıt için kullanıcı sözleşmesini kabul edin ve kişisel verilerin işlenmesine ayrıca onay verin.",
    close: "Kapat",
    loading: "Belge yükleniyor…",
    loadError: "Belge yüklenemedi.",
  },
};

/* REISOR_PRO_LEGAL_UI_V1 */

/* REISOR_PRO_LEGAL_SANITIZE_V1 */

function sanitizeLegalHtml(rawHtml) {
  const raw =
    String(
      rawHtml || ""
    );

  if (!raw) {
    return "";
  }

  try {
    const doc =
      new DOMParser()
        .parseFromString(
          raw,
          "text/html"
        );

    doc.querySelectorAll(
      "script,style,link,meta,title,base"
    ).forEach(
      (node) =>
        node.remove()
    );

    const body =
      doc.body;

    if (!body) {
      return "";
    }

    body.querySelectorAll(
      "script,style,link,meta,title,base"
    ).forEach(
      (node) =>
        node.remove()
    );

    body.querySelectorAll(
      "[style]"
    ).forEach(
      (node) =>
        node.removeAttribute(
          "style"
        )
    );

    return String(
      body.innerHTML || ""
    ).trim();

  } catch {

    return raw
      .replace(
        /<style[\s\S]*?<\/style>/gi,
        ""
      )
      .replace(
        /<script[\s\S]*?<\/script>/gi,
        ""
      )
      .replace(
        /<head[\s\S]*?<\/head>/gi,
        ""
      )
      .replace(
        /<\/?(?:html|body)[^>]*>/gi,
        ""
      )
      .replace(
        /\sstyle=(["'])[\s\S]*?\1/gi,
        ""
      )
      .trim();
  }
}


function languageCode() {
  const raw =
    String(
      i18next.resolvedLanguage ||
        i18next.language ||
        "ru"
    )
      .toLowerCase()
      .slice(0, 2);

  return COPY[raw] ? raw : "ru";
}

function readToken(role) {
  try {
    const scoped =
      String(
        localStorage.getItem(
          tokenKeyForRole(role)
        ) || ""
      ).trim();

    if (scoped) {
      return scoped;
    }

    return String(
      localStorage.getItem(
        LEGACY_TOKEN_KEY
      ) || ""
    ).trim();
  } catch {
    return "";
  }
}

function saveToken(role, token) {
  try {
    localStorage.setItem(
      tokenKeyForRole(role),
      String(token || "")
    );
  } catch {}
}

function clearToken(role, token = "") {
  try {
    localStorage.removeItem(
      tokenKeyForRole(role)
    );

    const legacy =
      String(
        localStorage.getItem(
          LEGACY_TOKEN_KEY
        ) || ""
      ).trim();

    if (
      legacy &&
      (
        !token ||
        legacy ===
          String(token).trim()
      )
    ) {
      localStorage.removeItem(
        LEGACY_TOKEN_KEY
      );
    }
  } catch {}
}

function apiErrorText(
  t,
  error,
  status
) {
  const code =
    String(
      error || ""
    ).toUpperCase();

  if (
    code ===
      "BAD_CREDENTIALS"
  ) {
    return t.badCredentials;
  }

  if (
    code ===
      "ALREADY_REGISTERED"
  ) {
    return t.exists;
  }

  if (
    code ===
      "TOO_MANY_REQUESTS"
  ) {
    return t.tooMany;
  }

  if (
    status === 404 ||
    status === 405 ||
    status === 502 ||
    status === 503
  ) {
    return t.apiUnavailable;
  }

  return t.generic;
}

async function apiRequest(
  path,
  {
    method = "GET",
    body,
    token = "",
  } = {}
) {
  return reisorProMarketRequest(
    path,
    { method, body, token }
  );
}

function phoneVerificationStorageKey(role) {
  return `reisor_market_phone_verify_v1:${String(role || "").toUpperCase()}`;
}

function savePhoneVerificationState(role, value) {
  try {
    if (!value) {
      localStorage.removeItem(phoneVerificationStorageKey(role));
      return;
    }

    localStorage.setItem(
      phoneVerificationStorageKey(role),
      JSON.stringify(value)
    );
  } catch {}
}

function readPhoneVerificationState(role) {
  try {
    const raw = localStorage.getItem(phoneVerificationStorageKey(role));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (!String(parsed.verificationToken || "").trim()) return null;
    return parsed;
  } catch {
    return null;
  }
}

function PasswordInput({
  value,
  onChange,
  placeholder,
  autoComplete,
}) {
  const [visible, setVisible] =
    useState(false);

  return (
    <div className="rpa-password-wrap">
      <input
        type={
          visible
            ? "text"
            : "password"
        }
        value={value}
        onChange={(event) =>
          onChange(
            event.target.value
          )
        }
        placeholder={placeholder}
        autoComplete={
          autoComplete
        }
        className="rpa-input rpa-input--password"
      />

      <button
        type="button"
        className="rpa-password-eye"
        onClick={() =>
          setVisible(
            (current) =>
              !current
          )
        }
        aria-label={
          visible
            ? "Hide password"
            : "Show password"
        }
      >
        {visible ? (
          <EyeOff
            size={20}
            aria-hidden="true"
          />
        ) : (
          <Eye
            size={20}
            aria-hidden="true"
          />
        )}
      </button>
    </div>
  );
}

export default function ReisorProMarketAuth({
  role,
  onBack,
}) {
  const t =
    COPY[languageCode()];

  const legalT =
    LEGAL_COPY[languageCode()];

  const normalizedRole =
    role === "EXPEDITOR"
      ? "EXPEDITOR"
      : "CARGO";

  const roleTitle =
    normalizedRole ===
    "EXPEDITOR"
      ? t.expeditor
      : t.cargo;

  /* REISOR_PRO_AUTH_CHOICE_V1 */

  // REISOR_PHONE_VERIFY_REGISTRATION_RESUME_V1
  const shouldResumePhoneRegistration = (() => {
    try {
      const current = new URL(
        window.location.href
      );

      const urlSignalsReturn =
        String(
          current.searchParams.get(
            "reisorPhoneVerification"
          ) || ""
        ).toLowerCase() === "done";

      const durableSignalsReturn =
        hasFreshPhoneVerificationReturnSignal();

      if (
        !urlSignalsReturn &&
        !durableSignalsReturn
      ) {
        return false;
      }

      const restored =
        readPhoneVerificationState(
          normalizedRole
        );

      if (!restored) return false;

      const expiresAtMs =
        restored.expiresAt
          ? new Date(
              restored.expiresAt
            ).getTime()
          : 0;

      return !(
        expiresAtMs &&
        expiresAtMs <= Date.now()
      );
    } catch {
      return false;
    }
  })();

  const [mode, setMode] =
    useState(() =>
      shouldResumePhoneRegistration
        ? "register"
        : "choice"
    );

  const [country, setCountry] =
    useState("UZ");

  const [phone, setPhone] =
    useState("");

  const [password, setPassword] =
    useState("");

  const [
    passwordConfirm,
    setPasswordConfirm,
  ] = useState("");

  const [busy, setBusy] =
    useState(false);

  const [error, setError] =
    useState("");

  const [
    phoneVerification,
    setPhoneVerification,
  ] = useState(null);

  const [
    phoneVerificationBusy,
    setPhoneVerificationBusy,
  ] = useState(false);

  const [
    phoneVerificationTransitionVisible,
    setPhoneVerificationTransitionVisible,
  ] = useState(false);

  const [
    agreementAccepted,
    setAgreementAccepted,
  ] = useState(false);

  const [
    personalDataConsent,
    setPersonalDataConsent,
  ] = useState(false);

  const [
    loginLegalRequirement,
    setLoginLegalRequirement,
  ] = useState(null);

  const [
    registrationLegalBundle,
    setRegistrationLegalBundle,
  ] = useState(null);

  const legalAccepted =
    agreementAccepted &&
    personalDataConsent;

  const [
    legalDoc,
    setLegalDoc,
  ] = useState(null);

  const [marketToken, setMarketToken] =
    useState(() =>
      readToken(normalizedRole)
    );

  const [user, setUser] =
    useState(null);

  const currentCountry =
    useMemo(
      () =>
        COUNTRIES.find(
          (item) =>
            item.code ===
            country
        ) ||
        COUNTRIES[0],
      [country]
    );

  const phoneVerificationVerified =
    mode === "register" &&
    String(phoneVerification?.status || "").toUpperCase() === "VERIFIED";

  function clearPhoneVerification() {
    setPhoneVerification(null);
    setPhoneVerificationBusy(false);
    savePhoneVerificationState(normalizedRole, null);
  }

  async function refreshPhoneVerificationStatus(explicitToken = "") {
    const verificationToken =
      String(
        explicitToken ||
        phoneVerification?.verificationToken ||
        ""
      ).trim();

    if (!verificationToken) {
      return null;
    }

    try {
      const { response, data } = await apiRequest(
        "/auth/phone-verification/status",
        {
          method: "POST",
          body: { verificationToken },
        }
      );

      if (!response.ok) {
        if ([404, 410].includes(Number(response.status))) {
          clearPhoneVerification();
        }
        return null;
      }

      const nextStatus =
        String(data?.status || "").toUpperCase();

      // Preserve the persisted registration draft during Android cold-start.
      // Password/passwordConfirm are intentionally never stored here.
      const persistedVerification =
        readPhoneVerificationState(
          normalizedRole
        );

      const next = {
        ...(persistedVerification || {}),
        ...(phoneVerification || {}),
        verificationToken,
        status: nextStatus,
        phoneNormalized: String(
          data?.phoneNormalized ||
          phoneVerification?.phoneNormalized ||
          persistedVerification?.phoneNormalized ||
          ""
        ),
        expiresAt:
          data?.expiresAt ||
          phoneVerification?.expiresAt ||
          persistedVerification?.expiresAt ||
          null,
        failureCode: String(data?.failureCode || ""),
      };

      setPhoneVerification(next);
      savePhoneVerificationState(normalizedRole, next);

      return next;
    } catch {
      return null;
    }
  }

  async function startPhoneVerification() {
    if (phoneVerificationBusy || busy) return;

    setError("");

    if (!phone.trim()) {
      setError(t.phoneRequired);
      return;
    }

    setPhoneVerificationTransitionVisible(true);
    setPhoneVerificationBusy(true);

    try {
      const { response, data } = await apiRequest(
        "/auth/phone-verification/start",
        {
          method: "POST",
          body: {
            role: normalizedRole,
            phone: phone.trim(),
            registrationCountry: country,
          },
        }
      );

      if (!response.ok || !data?.verificationToken || !data?.telegramUrl) {
        setPhoneVerificationTransitionVisible(false);
        setError(
          apiErrorText(
            t,
            data?.error,
            response.status
          )
        );
        return;
      }

      const next = {
        verificationToken: String(data.verificationToken),
        status: String(data.status || "PENDING").toUpperCase(),
        phoneNormalized: String(data.phoneNormalized || ""),
        expiresAt: data.expiresAt || null,
        failureCode: "",
        inputKey: `${country}|${phone.trim()}`,
        inputCountry: country,
        inputPhone: phone.trim(),
      };

      setPhoneVerification(next);
      savePhoneVerificationState(normalizedRole, next);

      const telegramUrl = String(data.telegramUrl || "").trim();
      const nativeTelegramUrl = telegramNativeMiniAppUrl(telegramUrl);

      if (nativeTelegramUrl) {
        let fallbackTimer = 0;

        const clearFallback = () => {
          if (fallbackTimer) {
            window.clearTimeout(fallbackTimer);
            fallbackTimer = 0;
          }

          document.removeEventListener(
            "visibilitychange",
            clearFallbackOnLeave
          );
        };

        const clearFallbackOnLeave = () => {
          if (document.visibilityState === "hidden") {
            clearFallback();
          }
        };

        document.addEventListener(
          "visibilitychange",
          clearFallbackOnLeave
        );

        fallbackTimer = window.setTimeout(() => {
          clearFallback();

          if (document.visibilityState !== "visible") {
            return;
          }

          try {
            window.location.assign(telegramUrl);
          } catch {
            try {
              window.location.href = telegramUrl;
            } catch {}
          }
        }, 1200);

        try {
          window.location.assign(nativeTelegramUrl);
        } catch {
          clearFallback();

          try {
            window.location.assign(telegramUrl);
          } catch {
            try {
              window.location.href = telegramUrl;
            } catch {}
          }
        }
      } else {
        try {
          window.location.assign(telegramUrl);
        } catch {
          try {
            window.location.href = telegramUrl;
          } catch {}
        }
      }
    } catch {
      setPhoneVerificationTransitionVisible(false);
      setError(t.apiUnavailable);
    } finally {
      setPhoneVerificationBusy(false);
    }
  }

  useEffect(() => {
    if (!phoneVerificationTransitionVisible) {
      return undefined;
    }

    const onResume = () => {
      if (document.visibilityState === "visible") {
        setPhoneVerificationTransitionVisible(false);
      }
    };

    const failSafe = window.setTimeout(() => {
      setPhoneVerificationTransitionVisible(false);
    }, 10000);

    window.addEventListener("focus", onResume);
    document.addEventListener("visibilitychange", onResume);

    return () => {
      window.clearTimeout(failSafe);
      window.removeEventListener("focus", onResume);
      document.removeEventListener("visibilitychange", onResume);
    };
  }, [phoneVerificationTransitionVisible]);

  useEffect(() => {
    if (mode !== "register") {
      return;
    }

    const restored = readPhoneVerificationState(normalizedRole);
    if (!restored) return;

    const expiresAtMs = restored.expiresAt
      ? new Date(restored.expiresAt).getTime()
      : 0;

    if (expiresAtMs && expiresAtMs <= Date.now()) {
      savePhoneVerificationState(normalizedRole, null);
      clearPhoneVerificationReturnSignal();
      return;
    }

    if (restored.inputCountry) {
      setCountry(String(restored.inputCountry));
    }

    if (restored.inputPhone) {
      setPhone(String(restored.inputPhone));
    }

    setPhoneVerification(restored);
    clearPhoneVerificationReturnSignal();
    void refreshPhoneVerificationStatus(restored.verificationToken);
    // Intentionally restore only when role/register mode changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, normalizedRole]);

  useEffect(() => {
    const onPhoneVerificationReturn = () => {
      const restored =
        readPhoneVerificationState(
          normalizedRole
        );

      if (!restored) return;

      const expiresAtMs =
        restored.expiresAt
          ? new Date(
              restored.expiresAt
            ).getTime()
          : 0;

      if (
        expiresAtMs &&
        expiresAtMs <= Date.now()
      ) {
        savePhoneVerificationState(
          normalizedRole,
          null
        );
        clearPhoneVerificationReturnSignal();
        return;
      }

      if (restored.inputCountry) {
        setCountry(
          String(restored.inputCountry)
        );
      }

      if (restored.inputPhone) {
        setPhone(
          String(restored.inputPhone)
        );
      }

      setPhoneVerification(restored);
      clearPhoneVerificationReturnSignal();

      // Return from Telegram must continue registration,
      // never fall back to the Login / Registration choice screen.
      setMode("register");
      setError("");

      const token =
        String(
          phoneVerification?.verificationToken ||
          restored.verificationToken ||
          ""
        ).trim();

      if (!token) return;

      void refreshPhoneVerificationStatus(
        token
      );
    };

    window.addEventListener(
      "phone-verification-return",
      onPhoneVerificationReturn
    );

    return () => {
      window.removeEventListener(
        "phone-verification-return",
        onPhoneVerificationReturn
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    normalizedRole,
    phoneVerification?.verificationToken,
  ]);

  useEffect(() => {
    if (mode !== "register") return undefined;

    let dead = false;
    let appStateHandle = null;

    const refreshOnNativeForeground = () => {
      if (dead) return;

      setPhoneVerificationTransitionVisible(false);

      const restored =
        readPhoneVerificationState(normalizedRole);

      const token =
        String(
          restored?.verificationToken ||
          phoneVerification?.verificationToken ||
          ""
        ).trim();

      const state =
        String(
          restored?.status ||
          phoneVerification?.status ||
          ""
        ).toUpperCase();

      if (
        !token ||
        [
          "VERIFIED",
          "CONSUMED",
          "FAILED",
          "EXPIRED",
          "SUPERSEDED",
        ].includes(state)
      ) {
        return;
      }

      void refreshPhoneVerificationStatus(token);
    };

    void CapApp.addListener(
      "appStateChange",
      ({ isActive }) => {
        if (isActive) {
          refreshOnNativeForeground();
        }
      }
    )
      .then((handle) => {
        if (dead) {
          void handle?.remove?.();
          return;
        }
        appStateHandle = handle;
      })
      .catch(() => {});

    return () => {
      dead = true;
      try {
        void appStateHandle?.remove?.();
      } catch {}
    };
    // Native Android onResume is the authoritative foreground signal.
    // Existing focus/visibility listeners remain as web-compatible fallbacks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mode,
    normalizedRole,
  ]);

  useEffect(() => {
    if (mode !== "register") return;

    const token = String(phoneVerification?.verificationToken || "").trim();
    const state = String(phoneVerification?.status || "").toUpperCase();

    if (!token || ["VERIFIED", "CONSUMED", "FAILED", "EXPIRED", "SUPERSEDED"].includes(state)) {
      return;
    }

    let dead = false;

    const refresh = () => {
      if (!dead) void refreshPhoneVerificationStatus(token);
    };

    const interval = window.setInterval(refresh, 1500);
    window.addEventListener("focus", refresh);

    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };

    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      dead = true;
      window.clearInterval(interval);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, phoneVerification?.verificationToken, phoneVerification?.status]);

  useEffect(() => {
    let cancelled = false;

    async function restore() {
      const token =
        readToken(normalizedRole);

      if (!token) {
        return;
      }

      try {
        const {
          response,
          data,
        } =
          await apiRequest(
            "/me",
            { token }
          );

        if (cancelled) {
          return;
        }

        if (
          response.ok &&
          data?.user?.role ===
            normalizedRole
        ) {
          saveToken(
            normalizedRole,
            token
          );

          setMarketToken(
            token
          );

          setUser(
            data.user
          );

          return;
        }

        if (
          response.status ===
            401
        ) {
          clearToken(
            normalizedRole,
            token
          );
          setMarketToken("");
          setUser(null);
        }
      } catch {
        // Do not destroy a stored token
        // merely because the network is temporarily unavailable.
      }
    }

    restore();

    return () => {
      cancelled = true;
    };
  }, [normalizedRole]);

  useEffect(() => {
    let active = true;
    if (mode !== "register") {
      setRegistrationLegalBundle(null);
      return () => { active = false; };
    }
    const accountType = normalizedRole === "EXPEDITOR" ? "expeditor" : "client";
    fetchUzLegalBundle(accountType, languageCode())
      .then((bundle) => { if (active) setRegistrationLegalBundle(bundle); })
      .catch(() => { if (active) setRegistrationLegalBundle(null); });
    return () => { active = false; };
  }, [mode, normalizedRole, i18next.language]);

  async function openLegalDoc(kind) {
    const accountType = normalizedRole === "EXPEDITOR" ? "expeditor" : "client";
    const title =
      kind === "offer"
        ? (normalizedRole === "EXPEDITOR" ? legalT.expeditorOffer : legalT.cargoOffer)
        : kind === "privacy"
          ? legalT.privacy
          : legalT.openPdConsent;

    setLegalDoc({ title, loading: true, error: "", html: "" });

    try {
      let bundle = registrationLegalBundle;
      if (!bundle) {
        bundle = await fetchUzLegalBundle(accountType, languageCode());
        setRegistrationLegalBundle(bundle);
      }
      let documents = [];

      if (kind === "offer") {
        documents = legalDocumentsByObligation(bundle, "AGREEMENT");
      } else if (kind === "privacy") {
        const doc = findLegalDocument(bundle, "REISOR-PRIVACY-001");
        if (doc) documents = [doc];
      } else {
        const doc = findLegalDocument(bundle, "REISOR-PD-CONSENT-001");
        if (doc) documents = [doc];
      }

      if (!documents.length) throw new Error("LEGAL_DOCUMENT_NOT_FOUND");

      const sections = [];
      for (const doc of documents) {
        const url = legalDocumentUrl(doc);
        if (!url) throw new Error("LEGAL_DOCUMENT_URL_MISSING");
        const response = await fetch(url, {
          method: "GET",
          credentials: "omit",
          headers: { Accept: "text/html,application/xhtml+xml" },
        });
        if (!response.ok) throw new Error(`HTTP_${response.status}`);
        const html = sanitizeLegalHtml(await response.text());
        if (!html) throw new Error("EMPTY_DOCUMENT");
        const safeTitle = String(doc.title || doc.documentNumber || "REISOR")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#039;");
        sections.push(`<section><h2>${safeTitle} · v${String(doc.version || "")}</h2>${html}</section>`);
      }

      setLegalDoc({ title, loading: false, error: "", html: sections.join("<hr/>") });
    } catch {
      setLegalDoc({ title, loading: false, error: legalT.loadError, html: "" });
    }
  }

  function switchMode(next) {
    setMode(next);
    setError("");
    setPassword("");
    setPasswordConfirm("");
    setAgreementAccepted(false);
    setPersonalDataConsent(false);
    setLoginLegalRequirement(null);
    setLegalDoc(null);
    clearPhoneVerification();
  }

  async function submit(
    event
  ) {
    event.preventDefault();

    if (busy) {
      return;
    }

    setError("");

    if (!phone.trim()) {
      setError(
        t.phoneRequired
      );
      return;
    }

    if (
      mode === "register" &&
      !phoneVerificationVerified
    ) {
      const status = String(phoneVerification?.status || "").toUpperCase();
      const failureCode = String(phoneVerification?.failureCode || "").toUpperCase();

      if (failureCode === "PHONE_MISMATCH") {
        setError(t.phoneVerifyMismatch);
      } else if (["EXPIRED", "SUPERSEDED"].includes(status)) {
        setError(t.phoneVerifyExpired);
      } else if (status === "FAILED") {
        setError(t.phoneVerifyFailed);
      } else {
        setError(t.phoneVerifyRequired);
      }
      return;
    }

    if (
      password.length < 6
    ) {
      setError(
        t.passwordShort
      );
      return;
    }

    if (
      mode === "register" &&
      password !==
        passwordConfirm
    ) {
      setError(
        t.mismatch
      );
      return;
    }

    if (
      (mode === "register" || loginLegalRequirement) &&
      !legalAccepted
    ) {
      setError(
        legalT.required
      );
      return;
    }

    setBusy(true);

    try {
      let legalV5 = null;
      if (mode === "register") {
        const accountType = normalizedRole === "EXPEDITOR" ? "expeditor" : "client";
        legalV5 = registrationLegalBundle || await fetchUzLegalBundle(accountType, languageCode());
      } else if (loginLegalRequirement?.presentedBundle) {
        legalV5 = {
          locale: loginLegalRequirement.locale || (languageCode() === "uz" ? "uz" : "ru"),
          presentedBundle: loginLegalRequirement.presentedBundle,
        };
      }

      const path =
        mode === "register"
          ? "/auth/register"
          : "/auth/login";

      const body = {
        role:
          normalizedRole,

        phone:
          phone.trim(),

        registrationCountry:
          country,

        password,

        ...(
          mode === "register"
            ? {
                phoneVerificationToken:
                  String(phoneVerification?.verificationToken || "").trim(),
              }
            : {}
        ),
      };

      if (
        mode === "register" ||
        loginLegalRequirement
      ) {
        body.passwordConfirm =
          passwordConfirm;

        body.agreementAccepted =
          agreementAccepted;

        body.personalDataConsent =
          personalDataConsent;

        body.legalLocale =
          legalV5?.locale || "ru";

        body.presentedBundle =
          legalV5?.presentedBundle || null;
      }

      const {
        response,
        data,
      } =
        await apiRequest(
          path,
          {
            method: "POST",
            body,
          }
        );

      if (
        !response.ok ||
        !data?.token ||
        !data?.user
      ) {
        const legalCode = String(data?.code || data?.error || "").toUpperCase();
        const requirement = data?.legal || data?.details?.legal || null;
        if ((legalCode === "LEGAL_REACCEPT_REQUIRED" || legalCode === "LEGAL_BUNDLE_STALE") && requirement?.presentedBundle) {
          setLoginLegalRequirement(requirement);
          setAgreementAccepted(false);
          setPersonalDataConsent(false);
          setError(legalT.required);
          return;
        }

        setError(
          apiErrorText(
            t,
            data?.error,
            response.status
          )
        );

        return;
      }

      const token =
        String(
          data.token
        );

      saveToken(
        normalizedRole,
        token
      );

      setMarketToken(
        token
      );

      setUser(
        data.user
      );

      setPassword("");
      setPasswordConfirm("");
      if (mode === "register") {
        clearPhoneVerification();
      }
      setLoginLegalRequirement(null);
      setAgreementAccepted(false);
      setPersonalDataConsent(false);
    } catch {
      setError(
        t.apiUnavailable
      );
    } finally {
      setBusy(false);
    }
  }

  async function logout() {
    const token =
      marketToken;

    setBusy(true);
    setError("");

    try {
      if (token) {
        await apiRequest(
          "/auth/logout",
          {
            method: "POST",
            token,
          }
        );
      }
    } catch {
      // Local logout still proceeds.
    } finally {
      clearToken(
        normalizedRole,
        token
      );
      setMarketToken("");
      setUser(null);
      setBusy(false);
    }
  }

  if (
    marketToken &&
    user
  ) {
    return (
      <ReisorProMarketBoard
        role={normalizedRole}
        roleTitle={roleTitle}
        marketToken={marketToken}
        user={user}
        onBack={onBack}
        onLogout={logout}
      />
    );
  }

  return (
    <main className="rpa-page">
      {phoneVerificationTransitionVisible ? (
        <div
          className="rpa-telegram-transition"
          role="status"
          aria-live="polite"
        >
          <div className="rpa-telegram-transition__panel">
            <div className="rpa-telegram-transition__brand">
              <span>REISOR</span>
              <span>PRO</span>
            </div>

            <div className="rpa-telegram-transition__shield">
              <ShieldCheck
                size={34}
                aria-hidden="true"
              />
            </div>

            <div className="rpa-telegram-transition__title">
              {t.phoneVerifyOpening}
            </div>

            <div className="rpa-telegram-transition__text">
              {t.phoneVerifyPending}
            </div>

            <div
              className="rpa-telegram-transition__loader"
              aria-hidden="true"
            >
              <span />
              <span />
              <span />
            </div>
          </div>
        </div>
      ) : null}

      <div className="rpa-glow rpa-glow--one" />
      <div className="rpa-glow rpa-glow--two" />

      <section className="rpa-shell">
        <div className="rpa-topbar">
          <button
            type="button"
            className="rpa-back"
            onClick={
              mode === "choice"
                ? onBack
                : () =>
                    switchMode(
                      "choice"
                    )
            }
          >
            <ArrowLeft
              size={19}
              aria-hidden="true"
            />

            {mode === "choice"
              ? t.back
              : t.backToChoice}
          </button>

          <ReisorProThemeToggle />
        </div>

        <header className="rpa-header">
          <div className="rpa-brand">
            <span>REISOR</span>
            <span className="rpa-brand__pro">
              PRO
            </span>
          </div>
          <div
            style={{
              marginTop: 6,
              fontSize: 12,
              fontWeight: 700,
              opacity: 0.72,
            }}
          >
            {t.brandContext}
          </div>

          <span className="rpa-role-pill">
            {roleTitle}
          </span>

          <h1>
            {mode === "choice"
              ? t.authChoiceTitle
              : mode === "register"
                ? t.registerTitle
                : t.loginTitle}
          </h1>

          <p>
            {mode === "choice"
              ? t.authChoiceText
              : mode === "register"
                ? t.registerText
                : t.loginText}
          </p>
        </header>

        {mode === "choice" ? (
          <div className="rpa-entry-card">

            <button
              type="button"
              className="rpa-entry-button rpa-entry-button--login"
              onClick={() =>
                switchMode(
                  "login"
                )
              }
            >
              {t.authChoiceLogin}
            </button>

            <button
              type="button"
              className="rpa-entry-button rpa-entry-button--register"
              onClick={() =>
                switchMode(
                  "register"
                )
              }
            >
              {t.authChoiceRegister}
            </button>

          </div>
        ) : null}

        <form
          className={`rpa-card ${
            mode === "choice"
              ? "rpa-card--hidden"
              : ""
          }`}
          onSubmit={submit}
        >
          <div className="rpa-field">
            <label
              htmlFor="rpa-country"
            >
              {t.country}
            </label>

            <NativeSafeSelect
              value={country}
              onChange={(nextCountry) => {
                setCountry(String(nextCountry || ""));
                setError("");
                clearPhoneVerification();
              }}
              options={COUNTRIES.map((item) => ({
                value: item.code,
                label: `${item.label} ${item.prefix}`,
              }))}
              className="rpa-country-select"
              buttonClassName="rpa-select rpa-country-select__button"
              menuClassName="rpa-country-select__menu"
              optionClassName="rpa-country-select__option"
              ariaLabel={t.country}
            />
          </div>

          <div className="rpa-field">
            <label
              htmlFor="rpa-phone"
            >
              {t.phone}
            </label>

            <div className="rpa-phone-wrap">
              <span className="rpa-phone-prefix">
                <Phone
                  size={17}
                  aria-hidden="true"
                />

                {
                  currentCountry.prefix
                }
              </span>

              <input
                id="rpa-phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={phone}
                onChange={(event) => {
                  setPhone(
                    event.target.value
                  );

                  setError("");
                  clearPhoneVerification();
                }}
                placeholder={
                  t.phonePlaceholder
                }
                className="rpa-input rpa-input--phone"
              />
            </div>
          </div>

          {mode === "register" ? (
            <div className="rpa-phone-verification">
              <button
                type="button"
                className={`rpa-phone-verify-button ${
                  phoneVerificationVerified
                    ? "rpa-phone-verify-button--verified"
                    : ""
                }`}
                onClick={() => void startPhoneVerification()}
                disabled={
                  busy ||
                  phoneVerificationBusy ||
                  phoneVerificationVerified
                }
              >
                <ShieldCheck
                  size={19}
                  aria-hidden="true"
                />
                {phoneVerificationVerified
                  ? t.phoneVerifyVerified
                  : phoneVerificationBusy
                    ? t.phoneVerifyOpening
                    : t.phoneVerifyButton}
              </button>

              {phoneVerification ? (
                <div
                  className={`rpa-phone-verify-status ${
                    phoneVerificationVerified
                      ? "rpa-phone-verify-status--verified"
                      : String(phoneVerification.status || "").toUpperCase() === "FAILED"
                        ? "rpa-phone-verify-status--error"
                        : ""
                  }`}
                >
                  {phoneVerificationVerified
                    ? `✓ ${t.phoneVerifyVerified}`
                    : String(phoneVerification.failureCode || "").toUpperCase() === "PHONE_MISMATCH"
                      ? t.phoneVerifyMismatch
                      : String(phoneVerification.status || "").toUpperCase() === "FAILED"
                        ? t.phoneVerifyFailed
                        : ["EXPIRED", "SUPERSEDED"].includes(String(phoneVerification.status || "").toUpperCase())
                          ? t.phoneVerifyExpired
                          : t.phoneVerifyPending}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="rpa-field">
            <label>
              {t.password}
            </label>

            <div className="rpa-input-icon-label">
              <LockKeyhole
                size={17}
                aria-hidden="true"
              />
            </div>

            <PasswordInput
              value={password}
              onChange={(value) => {
                setPassword(value);
                setError("");
              }}
              placeholder={
                t.passwordPlaceholder
              }
              autoComplete={
                mode === "register"
                  ? "new-password"
                  : "current-password"
              }
            />
          </div>

          {mode ===
            "register" && (
            <div className="rpa-field">
              <label>
                {
                  t.passwordConfirm
                }
              </label>

              <PasswordInput
                value={
                  passwordConfirm
                }
                onChange={(value) => {
                  setPasswordConfirm(
                    value
                  );

                  setError("");
                }}
                placeholder={
                  t.passwordPlaceholder
                }
                autoComplete="new-password"
              />
            </div>
          )}

          {(mode === "register" || loginLegalRequirement) ? (
            <div className="rpa-legal-consent" data-reisor-legal-reaccept={mode === "login" ? "market-v1" : undefined}>
              {mode === "login" && loginLegalRequirement ? (
                <>
                  <div style={{ marginBottom: 8, fontWeight: 800 }}>
                    {legalT.updated || "Условия REISOR обновились. Подтвердите текущую редакцию перед входом."}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: 10 }}>
                    {(loginLegalRequirement.documents || []).map((doc) => (
                      <button
                        key={`${doc.documentNumber}:${doc.version}`}
                        type="button"
                        onClick={() => {
                          const raw = String(doc.printUrl || doc.documentUrl || "").trim();
                          if (raw) window.open(raw, "_blank", "noopener,noreferrer");
                        }}
                        style={{
                          background: "transparent",
                          border: 0,
                          padding: 0,
                          color: "#2563eb",
                          textAlign: "left",
                          textDecoration: "underline",
                          cursor: "pointer",
                          fontSize: 13,
                        }}
                      >
                        {doc.title || doc.documentNumber} · v{doc.version}
                      </button>
                    ))}
                  </div>
                </>
              ) : null}
              <label className="rpa-legal-check">
                <input
                  type="checkbox"
                  checked={agreementAccepted}
                  onChange={(event) => {
                    setAgreementAccepted(event.target.checked);
                    setError("");
                  }}
                />
                <span>
                  {legalT.accept} {" "}
                  <button
                    type="button"
                    onClick={() => void openLegalDoc("offer")}
                  >
                    {normalizedRole === "EXPEDITOR" ? legalT.expeditorOffer : legalT.cargoOffer}
                  </button>
                  {" "}{legalT.platformRules}.
                </span>
              </label>

              <div style={{ margin: "-2px 0 10px 28px", fontSize: 12, color: "#94a3b8" }}>
                {legalT.privacy}: {" "}
                <button type="button" onClick={() => void openLegalDoc("privacy")}>
                  {legalT.open || legalT.privacy}
                </button>.
              </div>

              <label className="rpa-legal-check">
                <input
                  type="checkbox"
                  checked={personalDataConsent}
                  onChange={(event) => {
                    setPersonalDataConsent(event.target.checked);
                    setError("");
                  }}
                />
                <span>
                  {legalT.pdConsent} {" "}
                  <button
                    type="button"
                    onClick={() => void openLegalDoc("pdConsent")}
                  >
                    {legalT.openPdConsent}
                  </button>.
                </span>
              </label>
            </div>
          ) : null}

          {error ? (
            <div
              className="rpa-error"
              role="alert"
            >
              {error}
            </div>
          ) : null}

          <button
            type="submit"
            className="rpa-primary-button"
            disabled={
              busy ||
              (mode === "register" && !phoneVerificationVerified)
            }
          >
            <UserRoundCheck
              size={20}
              aria-hidden="true"
            />

            {busy
              ? t.loading
              : mode ===
                  "register"
                ? t.register
                : t.login}
          </button>

          <button
            type="button"
            className="rpa-switch"
            onClick={() =>
              switchMode(
                mode === "register"
                  ? "login"
                  : "register"
              )
            }
            disabled={busy}
          >
            <span>
              {mode === "register"
                ? t.already
                : t.noAccount}
            </span>

            <strong>
              {mode === "register"
                ? t.loginLink
                : t.registerLink}
            </strong>
          </button>
        </form>

        <div className="rpa-security">
          <ShieldCheck
            size={22}
            strokeWidth={1.8}
            aria-hidden="true"
          />

          <p>
            {t.safety}
          </p>
        </div>

        <button
          type="button"
          className="rpa-support-button"
          onClick={
            openReisorProSupport
          }
        >
          <Phone
            size={19}
            aria-hidden="true"
          />

          <span>
            {t.support}
          </span>
        </button>
      </section>

      {legalDoc ? (
        <div
          className="rpa-legal-modal"
          role="dialog"
          aria-modal="true"
        >
          <section className="rpa-legal-sheet">
            <header>
              <strong>
                {legalDoc.title}
              </strong>

              <button
                type="button"
                onClick={() =>
                  setLegalDoc(null)
                }
              >
                ×
              </button>
            </header>

            <div className="rpa-legal-body">
              {legalDoc.loading ? (
                <div className="rpa-legal-state">
                  {legalT.loading}
                </div>
              ) : legalDoc.error ? (
                <div className="rpa-legal-state rpa-legal-state--error">
                  {legalDoc.error}
                </div>
              ) : (
                <div
                  className="rpa-legal-document"
                  dangerouslySetInnerHTML={{
                    __html:
                      legalDoc.html,
                  }}
                />
              )}
            </div>

            <footer>
              <button
                type="button"
                onClick={() =>
                  setLegalDoc(null)
                }
              >
                {legalT.close}
              </button>
            </footer>
          </section>
        </div>
      ) : null}
    </main>
  );
}
